/**
 * mr-io-absorb.js -- "The Names" world-class pass, 2026-07-14.
 *
 * Purpose: Infinite Options (IO) is MuslimsReads' own backend for the
 * beneficiary fields -- the customer should never perceive it as a
 * third-party app. This script rewrites IO's own rendered CAPTIONS
 * (field questions/labels) into MuslimsReads' host voice, per
 * MR-Commitment-Ceremony.md Movement II (points 5-6) and Law 4 ("Every
 * input speaks like a host"). Caption rewriting never touches any
 * `name`, `value`, `id`, or `for` attribute -- only visible text.
 *
 * 2026-07-19 FOUNDER-APPROVED ADDITION -- PROOF FIELD RETIRED: proof is
 * now a statement on the page, not a question in the form. The
 * `Proof Requested` line-item property is removed from every product.
 * For products whose beneficiary fields come from the IO app block
 * (?view=quran and any other IO-configured product), the app markup
 * itself cannot be edited from the theme, so this script neutralises the
 * field theme-side the moment IO mounts it:
 *   - every input named  properties[Proof Requested]  has its `name`
 *     attribute REMOVED. LIVE-VERIFIED IO STRUCTURE (preview,
 *     2026-07-19): the visible checkbox carries NO name at all -- IO
 *     submits this field exclusively through a hidden companion input
 *     inside the same <fieldset> (value "" when unticked -- the exact
 *     `Proof Requested: ""` bug). De-naming that hidden input therefore
 *     kills the key for BOTH ticked and unticked states -- FormData only
 *     includes elements that HAVE a name, so the key is absent from the
 *     cart entirely (not "");
 *   - the field's own container (checkbox + caption) is hidden by
 *     climbing from each retired input's fieldset/label to the highest
 *     ancestor that holds no OTHER named property field, with a
 *     text-match fallback for stray caption elements. LIVE FINDINGS
 *     (2026-07-19, this exact page): (a) IO's engine re-applies inline
 *     display styles after mount, which undid a plain
 *     `style.display='none'` hide -- the hide therefore uses a data
 *     attribute (`data-mr-proof-hidden`) + one injected stylesheet rule,
 *     and the observer also watches attribute mutations; (b) this
 *     theme's own base.css carries `#infiniteoptions-container > div
 *     { display: grid !important }` (ID specificity 1-0-1), which beat a
 *     plain `[data-mr-proof-hidden]` (0-1-0) rule on the display
 *     property -- the injected rule therefore includes
 *     `#infiniteoptions-container div[data-mr-proof-hidden]` (1-1-1)
 *     variants plus a layout-collapse fallback (height/margin/padding 0)
 *     so the field can never occupy space even if a future rule contests
 *     display again.
 * STRICTLY scoped to that one property name -- every other IO field
 * (including the Qur'an inscription checkbox `Name on first page
 * (Makkah)`, which must keep working) is untouched: the climb stops at
 * any ancestor containing a different named properties[...] control.
 * The former proof caption/answer-text relabels were removed from the
 * maps below since the field no longer renders. This is theme JS only --
 * the IO app config is NOT modified, so the live theme keeps its current
 * behaviour until this theme is published.
 *
 * WHY TEXT/ATTR WORK HERE IS SAFE (investigated 2026-07-14, see memory
 * reference_infinite_options_technical_findings): a <label>'s text
 * content and a control's submitted value are separate DOM state;
 * confirmed with a real add-to-cart test byte-for-byte. Removing a
 * `name` attribute is the standard, browser-native way to exclude a
 * control from FormData (same technique already used by
 * mr-fulfilment-personalise.js for the du'a textarea).
 *
 * LIVE-VERIFIED DOM QUIRK (2026-07-14): IO duplicates a field caption as
 * a screen-reader-only <legend> AND a visible <label> with NO `for`
 * attribute. Caption matching therefore covers ALL <label>/<legend>
 * elements, excluding only ones that wrap a form control themselves.
 *
 * ROBUSTNESS: applies once on load (or the moment IO's fields mount, via
 * MutationObserver -- IO loads asynchronously, same pattern as
 * mr-io-loading.js) and keeps observing afterward. Every rewrite is
 * idempotent -- rewritten text no longer matches the source map, a
 * de-named input no longer matches the property selector, and re-setting
 * an already-set attribute is guarded -- so this script's own writes
 * never loop the observer.
 *
 * Shared section file (sections/mr-s-product-commitment.liquid) means
 * this script loads on every conviction-zone product. It is written
 * defensively (exact-text-match / exact-property-name only) so it
 * silently does nothing on any product without matching IO markup.
 */
(function () {
  'use strict';

  var IO_BLOCK_SELECTOR = '[id$="__io_app_block"]';
  var PROOF_PROPERTY_SELECTOR = 'input[name="properties[Proof Requested]"]';
  var HIDE_ATTR = 'data-mr-proof-hidden';
  var STYLE_ID = 'mr-proof-retire-style';
  /* See header, live finding (b): the ID-prefixed variants out-specify
     base.css's own `#infiniteoptions-container > div{display:grid
     !important}`; the collapse properties are belt-and-braces so the
     retired field never occupies layout space regardless. */
  var HIDE_CSS =
    '#infiniteoptions-container div[' + HIDE_ATTR + '],' +
    '#infiniteoptions-container [' + HIDE_ATTR + '],' +
    '.infiniteoptions-container div[' + HIDE_ATTR + '],' +
    '[' + HIDE_ATTR + ']' +
    '{display:none !important;visibility:hidden !important;height:0 !important;min-height:0 !important;max-height:0 !important;overflow:hidden !important;margin:0 !important;padding:0 !important;border:0 !important;pointer-events:none !important;}';

  /* Proof caption texts to hide if the container-climb below could not
     swallow them (covers both IO's original caption and the host-voice
     rewrite this script used to apply before 2026-07-19). */
  var PROOF_CAPTION_TEXTS = [
    'Would you like video/photo proof?',
    'We will film your fulfilment and email you the proof.'
  ];

  /* Caption-level relabels: exact original text -> host-voice replacement.
     "Beneficiary Gender" / "Beneficiary Status" quoted directly from
     MR-Commitment-Ceremony.md, Movement II, points 5-6. "Beneficiary
     Full Name" shortened conservatively per Law 4. The proof caption
     entry was removed 2026-07-19 -- the proof field is retired. */
  var CAPTION_MAP = {
    'Beneficiary Full Name': 'Their name',
    'Beneficiary Gender': 'How should we address them?',
    'Beneficiary Status': "Would you like us to make du'a for their healing?"
  };

  /* Checkbox answer-text relabels (plain sibling text node of the
     checkbox inside its <label> -- NOT the submitted `value`). The
     'Yes, send me proof by email' entry was removed 2026-07-19 with the
     proof field itself. The Qur'an inscription entry stays -- that
     checkbox keeps working exactly as before. */
  var CHECKBOX_TEXT_MAP = {
    "Yes, write the beneficiary's name": 'Yes, write their name'
  };

  function ensureHideStyle() {
    var existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== HIDE_CSS) existing.textContent = HIDE_CSS;
      return;
    }
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = HIDE_CSS;
    document.head.appendChild(style);
  }

  function hideEl(el) {
    if (!el.hasAttribute(HIDE_ATTR)) el.setAttribute(HIDE_ATTR, '1');
  }

  function rewriteCaptions(root) {
    var els = root.querySelectorAll('label, legend');
    els.forEach(function (el) {
      /* Skip answer-wrapping labels (the checkbox's own <label> contains
         the <input> itself) -- those are handled by
         rewriteCheckboxAnswers below, never here. */
      if (el.querySelector('input, select, textarea')) return;
      var text = el.textContent.trim();
      if (Object.prototype.hasOwnProperty.call(CAPTION_MAP, text)) {
        el.textContent = CAPTION_MAP[text];
      }
      if (PROOF_CAPTION_TEXTS.indexOf(text) !== -1) {
        hideEl(el); /* orphaned proof caption fallback */
      }
    });
  }

  function rewriteCheckboxAnswers(root) {
    var labels = root.querySelectorAll('label');
    labels.forEach(function (label) {
      if (!label.querySelector('input[type="checkbox"]')) return;
      var textNode = null;
      for (var i = 0; i < label.childNodes.length; i++) {
        var node = label.childNodes[i];
        if (node.nodeType === 3 && node.textContent.trim().length > 0) {
          textNode = node;
          break;
        }
      }
      if (!textNode) return;
      var text = textNode.textContent.trim();
      if (Object.prototype.hasOwnProperty.call(CHECKBOX_TEXT_MAP, text)) {
        textNode.textContent = textNode.textContent.replace(text, CHECKBOX_TEXT_MAP[text]);
      }
    });
  }

  /* 2026-07-19: retire the Proof Requested field entirely (see header).
     Pass 1 de-names EVERY matching input (in verified IO markup that is
     the hidden companion -- the visible checkbox has no name of its own)
     so the key can never submit -- absent, not "". Pass 2 hides the
     field's container: starting from each retired input's
     fieldset/label, climb while the parent holds no OTHER named property
     field, so the hidden wrapper swallows the field's own checkbox +
     caption but can never swallow a neighbouring field (e.g. the Qur'an
     inscription checkbox). */
  function removeProofField(root) {
    var named = Array.prototype.slice.call(root.querySelectorAll(PROOF_PROPERTY_SELECTOR));
    named.forEach(function (input) {
      input.setAttribute('data-mr-proof-retired', '1');
      input.removeAttribute('name');
      if (input.type === 'checkbox') input.checked = false;
    });

    var retired = Array.prototype.slice.call(root.querySelectorAll('[data-mr-proof-retired]'));
    retired.forEach(function (input) {
      var candidate = input.closest('fieldset') || input.closest('label') || input;
      var parent = candidate.parentElement;
      while (parent && !parent.matches(IO_BLOCK_SELECTOR) && parent !== document.body) {
        var otherFields = parent.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]');
        if (otherFields.length > 0) break;
        candidate = parent;
        parent = parent.parentElement;
      }
      hideEl(candidate);
    });
  }

  function absorb(ioBlock) {
    ensureHideStyle();
    removeProofField(ioBlock);
    rewriteCaptions(ioBlock);
    rewriteCheckboxAnswers(ioBlock);
  }

  function wire(ioBlock) {
    absorb(ioBlock);
    var observer = new MutationObserver(function () {
      absorb(ioBlock);
    });
    /* attributes:true (2026-07-19): IO's engine rewrites inline styles
       and could strip the hide attribute on a re-render -- attribute
       mutations must re-trigger absorb. All writes above are guarded, so
       our own mutations settle instead of looping. */
    observer.observe(ioBlock, { childList: true, subtree: true, characterData: true, attributes: true });
  }

  function init(root) {
    root = root || document;
    root.querySelectorAll(IO_BLOCK_SELECTOR).forEach(wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  window.MRIOAbsorb = { init: init };
})();
